/**
 * Activity-surface copy, base (legal) dialect. The en catalog is complete by
 * definition - its object type IS the key schema every other locale overrides.
 * Sourced from the activity wizard (`ActivityWizardForm.tsx`), whose step
 * labels, search copy, and rate hint speak of "matters"; the business dialect
 * overrides them to "projects".
 */
export const activity = {
    wizard: {
        /** Step 1 label in the matter/project picker's progress indicator. */
        selectProjectStep: 'Select matter',
        /** Accessible label for the matter/project search input. */
        searchAriaLabel: 'Search clients and matters',
        /** Placeholder text for the matter/project search input. */
        searchPlaceholder: 'Search clients or matters...',
        /** Empty state shown when a search query matches nothing. */
        noSearchResults: 'No clients or matters match your search',
        /** Empty state shown when the account has no active matters/projects at all. */
        noActiveProjects: 'No active matters found',
        /** Warning shown when no default rate is configured for the selected unit type. */
        noDefaultRate: 'No rate set. Enter a rate.',
        /** Zod validation message: no matter selected on step 1. */
        projectRequired: 'Matter is required',
        /** Step 2 fee-type tile section heading. */
        feesTileHeading: 'Fees',
        /**
         * Step 2 disbursement-type tile section heading. Must stay distinct from
         * expensesTileHeading in every dialect - the two groups render back to
         * back on the same step (business: 'Billable costs' / 'Expenses').
         */
        disbursementsTileHeading: 'Disbursements',
        /** Step 2 "Add an expense" row section heading (routes to the Expenses feature; same word in both dialects). */
        expensesTileHeading: 'Expenses',
        /** Step 2 "Add an expense" tile hint, explaining what belongs in the Expenses feature instead. */
        expenseHint: "Money paid to a third party, like advocate or sheriff fees, captured with the supplier's invoice and VAT.",
        /**
         * Rate hint under the wizard's rate field, one leaf per place a resolved
         * rate can come from. `{{feeEarner}}` arrives already in the possessive
         * ("Thabo Nkosi's"). The amount is already visible in the rate field.
         */
        rateHint: {
            /** The performer's own rate on this matter/project. */
            feeEarnerProject: '{{feeEarner}} matter rate',
            /** The performer's own rate on the client. */
            feeEarnerClient: '{{feeEarner}} client rate',
            /** The shared rate stored on the matter/project. */
            sharedProject: 'Shared matter rate',
            /** The shared rate stored on the client. */
            sharedClient: 'Shared client rate',
            /** The performer's rate from the Team tab. */
            team: '{{feeEarner}} Team rate',
            /** A practice-wide rate (per page, per km, flat email rate). */
            account: 'Practice rate',
        },
        /**
         * Lead-in text before the deeplinked invoice number, shown when this
         * entry already sits on an invoice line item (sibling of the read-only
         * banner, not a replacement - the entry can still be editable while
         * carrying this notice). The invoice number itself renders as a link
         * appended after this string, so it stays a whole sentence fragment
         * rather than a composed noun.
         */
        linkedInvoiceNotice: 'This entry is on invoice',
        /**
         * "Performed by" option meaning nobody is recorded for the entry, for fees
         * and disbursements alike (work by someone who has left, an imported row
         * with no email).
         */
        noFeeEarnerOption: 'No fee earner',
    },
    /** Bulk activity import results. */
    import: {
        /** Row warning on a fee created from a row with no User Email. */
        noFeeEarnerWarning: 'No User Email, so this fee was imported with no fee earner.',
    },
    /**
     * The internal file note on a time entry (meeting minutes, attendees, the
     * detail behind the line). Never shown on the invoice or to the client.
     */
    note: {
        /** Field label, the add-note button and the expanded editor's title. */
        label: 'File note',
        /** Button that reveals the note field on an entry with no note. */
        add: 'Add file note',
        /** Hint under the field; the same in every dialect. */
        hint: 'Internal only. Never shown on the invoice or to the client.',
        /** Placeholder in the empty note field. */
        placeholder: 'Meeting minutes, who attended, or the detail behind this entry',
        /** Muted line under the field; `{{when}}` is a long South African date and time. */
        lastEdited: 'Last edited {{when}}',
        /** Notice on an invoiced entry: the details lock but the note stays editable. */
        invoicedNotice: 'This activity has been invoiced, so its details are locked. You can still edit the file note.',
        /** Submit button on an invoiced entry, where only the note can change. */
        saveInvoiced: 'Save file note',
        /** Tooltip and accessible name of the note marker on an activity row. */
        markerLabel: 'Open file note',
    },
    /**
     * The "Disbursement" marker badge (`components/activity/DisbursementBadge.tsx`),
     * shared by every surface that flags disbursement state (activity cards, the
     * diary, the calendar/email detail views, and the invoice omnibox expense lane).
     */
    badge: {
        /** Standard info-variant pill. */
        disbursement: 'Disbursement',
        /** The diary's compact default-variant pill. */
        disbursementAbbreviated: 'Disb.',
        /** The omnibox lane heading's info-variant pill. */
        disbursementPlural: 'Disbursements',
    },
    /**
     * Per-activity-type narration patterns the chat agent is told to imitate
     * when it writes the description that lands on the client's invoice.
     * Guidance, not validators - the agent adapts them to context. One key per
     * `ClientProjectActivityType` (`@packages/types`). Rendered by the backend's
     * `formatActivityDescriptionPatterns`, which filters the deprecated
     * third-party-cost types (ADVOCATE, CORRESPONDENT_ATTORNEY, SHERIFF,
     * GOVERNMENT_GAZETTE) out of all rendered guidance - those keys stay here
     * only so the table is total over the enum.
     *
     * A `{...}` segment is OPTIONAL: the agent includes it only when the user
     * actually supplied that detail. The subject clause is written that way
     * because it is the one slot the user routinely leaves out ("log 5 minutes
     * on a call with the agent"), and a slot that looks obligatory gets filled
     * from the only thing to hand - the matter name - which the invoice line
     * already carries. The convention is spelled out for the model once, in
     * `ACTIVITY_DESCRIPTION_PATTERN_LEGEND` (agents/chat/activity-patterns.ts),
     * rendered above every copy of this block.
     */
    descriptionPatterns: {
        MEETING: '"Consultation with [person(s)]{ re: [topic]}" or "Meeting with [people] at [location]"',
        // The subject clause reads "with subject", not "re:", so a chat-logged
        // email entry matches the line the email reconciler writes
        // (`buildEmailActivityDescription`, client-project-activity domain) and
        // one invoice carries one voice. The other types keep the "re:" dialect.
        EMAIL: '"Review of email from [sender]{ with subject [subject]}" or "Drafting and sending of email{ with subject [subject]}"',
        PHONE: '"Telephone attendance with [person]{ re: [topic]}"',
        MESSAGE: '"Message attendance with [person]{ re: [topic]}" or, for a voice note, "Attendance to voice note from [person]{ re: [topic]}"',
        COURT_ATTENDANCE: '"Appearance at [court]{ re: [purpose]}"',
        DOCUMENT_REVIEW: '"Review of [document type]{ re: [purpose]}"',
        RESEARCH: '"Legal research re: [topic]"',
        DRAFTING: '"Drafting of [document type] [between parties]{ re: [purpose]}"',
        TRAVEL_CAR: '"Travel to [destination]{ re: [purpose]}"',
        PRINTING: '"Printing and photocopying - [N] pages"',
        POSTAGE: '"Postage and courier charges"',
        ADVOCATE: '"Advocate fees - [counsel name] [SC] re: [purpose]"',
        CORRESPONDENT_ATTORNEY: '"Correspondent attorney fees - [firm name]"',
        SHERIFF: '"Sheriff\'s fees [- purpose, district]"',
        GOVERNMENT_GAZETTE: '"Government Gazette publication - [notice type]"',
        GENERAL: 'free text describing the work; set unitType and isDisbursement via the top-level fields on the activity.',
    },
    /** Activities report (CSV + PDF export). Only the practice-varying labels live here; invariant headers (Date, Client Name, ...) stay at the render sites. */
    report: {
        /** CSV column header for the client-project reference. */
        projectReferenceColumn: 'Matter Reference',
        /** CSV column header for the client-project name. */
        projectNameColumn: 'Matter Name',
        /** PDF composite column header (reference + name in one cell). */
        projectColumn: 'Matter',
        /** PDF filter-summary item when N client-projects are filtered; interpolates {{total}}. */
        projectFilterCount: 'Matters: {{total}}',
    },
};
